import type { ReactElement } from "react"
import { Fragment } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useDispatch, useSelector } from "react-redux"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { ModelSwitchDialog } from "../../../components/dialogs/BaseSimpleDialog"
import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { Model } from "../../../types/generated/api/Model"
import type { ModelListCreate } from "../../../types/generated/api/ModelListCreate"
import type { PaginatedModelListCreateList } from "../../../types/generated/api/PaginatedModelListCreateList"
import { setCurrentModel } from "../Slice"
import type { SwitchDialogProps } from "../interfaces"

/**
 * Dialog component that handles model state switching functionality
 *
 * @param {boolean} isOpen - Controls the visibility state of the dialog
 * @param {Dispatch} setIsOpen - Function to handle dialog close state
 * @param {Model[]} challengerModels - List of challenger models in the project
 * @returns {ReactElement} Switch button with associated dialog
 */
export function SwitchDialog(props: Readonly<SwitchDialogProps>): ReactElement {
  const { isOpen, setIsOpen } = props

  const projectId = useSelector((state: RootState) => state.project.currentProject?.uuid)

  const currentModel = useSelector((state: RootState) => state.models.currentModel)

  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  const { t } = useTranslation("models")

  const isModelPendingAction = currentModel?.state === "pending-action"

  const { data: challengerModels, isFetching } = useQuery<AxiosResponse<PaginatedModelListCreateList>, AxiosError>(
    ["models", projectId, "challenger"],
    () =>
      KonanAPI.fetchModels({
        projectId: projectId as string,
        state: ["challenger"],
      }),
    {
      refetchOnMount: true,
    },
  )

  // Mutation for switching model state
  // Includes success/error handling and query invalidation
  const promoteModelMutation = useMutation<
    AxiosResponse,
    AxiosError,
    {
      projectId: string | undefined
      modelUUID: string
      newState: string
    }
  >(KonanAPI.switchModelState)

  /**
   * Handles model state promotion
   * @param {string} newState - The target state for the model
   * @param {Model} model - The model to replace with the current model if it is a different model
   */
  async function handleModelStateSwitch(newState: string, model: Model): Promise<void> {
    const isReplacing = currentModel?.uuid !== model?.uuid

    try {
      isReplacing &&
        (await promoteModelMutation.mutateAsync({
          projectId: projectId,
          modelUUID: model?.uuid,
          newState: "disabled",
        }))

      await promoteModelMutation.mutateAsync({
        projectId: projectId,
        modelUUID: currentModel?.uuid as string,
        newState: newState,
      })

      if (isReplacing) {
        NotificationUtils.toast(t("toasts.switch_replaced", { state: newState, name: model?.name ?? "" }), {
          severity: "success",
        })
      } else {
        NotificationUtils.toast(t("toasts.switch_success", { name: currentModel?.name, state: newState }), {
          severity: "success",
        })
      }

      window.location.pathname.includes("/models") &&
        dispatch(
          setCurrentModel({
            ...currentModel,
            state: newState,
          } as ModelListCreate | Model),
        )

      setIsOpen(false)

      // Invalidate react-query queries
      await Promise.all([
        queryClient.invalidateQueries("models"),
        queryClient.invalidateQueries(["model", currentModel?.uuid]),
        queryClient.invalidateQueries(["overviewModels", projectId]),
        queryClient.invalidateQueries(["modelHistory", projectId]),
      ])
    } catch {
      if (isReplacing) {
        NotificationUtils.toast(t("toasts.switch_replace_failed", { name: model?.name, current: currentModel?.name }), {
          severity: "error",
        })
      } else {
        NotificationUtils.toast(t("toasts.switch_failed", { name: currentModel?.name, state: newState }), {
          severity: "error",
        })
      }
    }
  }

  return (
    <Fragment>
      {/* Model switch dialog */}
      {isOpen && (
        <ModelSwitchDialog
          open={isOpen}
          name={isModelPendingAction ? currentModel?.name : t("dialogs.switch.title")}
          mode={isModelPendingAction ? "pending-model-switch" : "model-switch"}
          modelState={isModelPendingAction ? "pending-action" : (currentModel?.state as Model.state)}
          onAccept={(newModelState, modelToReplace) =>
            handleModelStateSwitch(newModelState, modelToReplace ?? (currentModel as Model))
          }
          isLoading={promoteModelMutation.isLoading}
          disableMainBtn={promoteModelMutation.isLoading}
          onClose={() => {
            setIsOpen(false)
            //  To prevent flickering of alerts when closing the dialog
            setTimeout(() => promoteModelMutation.reset(), 300)
          }}
          challengerModels={!isFetching ? (challengerModels?.data.results as Model[]) : []}
        />
      )}
    </Fragment>
  )
}
